import { useState } from "react";
import { CONNECTOR_GROUP_HINT, CONNECTOR_GROUP_LABEL } from "../map/presets";
import { tierBadgeText, type ConnectorLayerEntry } from "../map/connectorLayer";
import "./connectors.css";

interface Props {
  entries: readonly ConnectorLayerEntry[];
  enabled: ReadonlySet<string>;
  onToggle: (layerId: string) => void;
  /** Layer ids that are on but whose feed has no mappable features. */
  emptyLayers?: ReadonlySet<string>;
  onHover?: (h: { label: string; describe: string } | null) => void;
}

/** "Connected sources" palette group — one toggle per registry connector. */
export function ConnectorLayerGroup({ entries, enabled, onToggle, emptyLayers, onHover }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  if (entries.length === 0) return null;
  const on = entries.filter((e) => enabled.has(e.layerId)).length;
  return (
    <section className={`layer-group ${collapsed ? "is-collapsed" : ""}`} data-testid="connector-layer-group">
      <button
        type="button"
        className="layer-group-head"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        aria-label={`${CONNECTOR_GROUP_LABEL} layers (${on} of ${entries.length} on)`}
        title={CONNECTOR_GROUP_HINT}
      >
        <span className="layer-group-name mono">{CONNECTOR_GROUP_LABEL}</span>
        <span className="layer-group-meta mono">{on}/{entries.length}</span>
        <span className="layer-group-chevron mono">{collapsed ? "▶" : "▼"}</span>
      </button>
      {!collapsed && (
        <div className="layer-toggles">
          {entries.map((e) => {
            const isOn = enabled.has(e.layerId);
            const isEmpty = isOn && (emptyLayers?.has(e.layerId) ?? false);
            const note = isEmpty ? " — no mappable data right now, so nothing draws on the map" : "";
            const badge = tierBadgeText(e.tier);
            const toggle = () => onToggle(e.layerId);
            return (
              <div
                key={e.layerId}
                className={`layer-toggle ${isOn ? "on" : "off"}${isEmpty ? " is-empty" : ""}`}
                role="checkbox"
                aria-checked={isOn}
                tabIndex={0}
                title={`${e.describe}${note}`}
                aria-label={`${e.label} — ${e.describe}${note}`}
                onMouseEnter={() => onHover?.({ label: e.label, describe: e.describe })}
                onMouseLeave={() => onHover?.(null)}
                onFocus={() => onHover?.({ label: e.label, describe: e.describe })}
                onBlur={() => onHover?.(null)}
                onClick={toggle}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); toggle(); }
                }}
              >
                <span className="row">
                  <span className="swatch" style={{ background: e.color }} />
                  <span>{e.label}</span>
                  <span className={`connector-tier-badge connector-tier-badge--${e.tier}`}>{badge}</span>
                </span>
                <span className="mono caption">{isEmpty ? "no data" : isOn ? "on" : "off"}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
